import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { getMyVehicles } from '../api/userVehiclesApi';

/**
 * Canonical hook for reading the signed-in user's registered
 * vehicles.
 *
 * Sources data from `GET /api/vehicles/mine` — the same endpoint
 * every screen (dashboard preview, My Vehicles, vehicle detail)
 * uses, so no two views can render inconsistent lists. Scoping is
 * derived from the JWT server-side; we do not pass any identifier
 * from the client.
 *
 * State machine:
 *   isLoading = true, error = null      → first fetch in flight
 *   isLoading = false, error != null    → request failed; caller
 *                                         should render a retry state
 *   vehicles = [], both false/null       → auth resolved, request
 *                                         succeeded, genuinely empty
 *   vehicles.length > 0                  → populated
 *
 * Auth boot: while `AuthContext.isBooting` is true we do NOT fetch
 * and hold `vehicles = []` with `isLoading = true`, so callers can
 * treat the "still loading" state as "boot pending". This is the
 * key to avoiding the "No vehicles yet" flash on a cold refresh.
 *
 * Auth change: the fetch is keyed on `userId`. Signing out or
 * signing in a different account triggers a fresh fetch and drops
 * the previous list.
 */
export function useUserVehicles() {
  const { user, isBooting } = useAuth();
  const userId = user?.id || null;

  const [vehicles, setVehicles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    if (!userId) {
      setVehicles([]);
      setError(null);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const list = await getMyVehicles();
      setVehicles(list);
    } catch (err) {
      const message = err?.response?.data?.message
        || err?.message
        || 'Could not load your vehicles.';
      setError(message);
      // Deliberately do NOT clear `vehicles` on error — if a fetch
      // fails during a refresh the previous list stays on screen so
      // the retry banner sits above real content, not empty state.
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (isBooting) {
      // Hold "loading" until auth is resolved so no wrong empty
      // state flashes for the ~200ms of AuthContext boot.
      setIsLoading(true);
      return;
    }
    refresh();
  }, [userId, isBooting, refresh]);

  // No `is_primary` column in the registry — the first entry from
  // the deterministic backend ordering (active DESC, created_at DESC)
  // is treated as the customer's "primary" for the dashboard preview.
  const primary = vehicles[0] || null;

  return { vehicles, primary, isLoading, error, refresh };
}
